import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AppAuthService } from '../auth/app-auth-service';
import { AuthType } from '../../shared/enums/AuthType.enum';

const REFRESH_TOKEN_PATH = '/refresh-token';
const LOCAL_AUTH_PATH = '/pos-middleware/v1/auth/';
const LOCAL_HEALTH_PATH = '/pos-middleware/v1/actuator/health';

// Routes that own their own navigation: the device setup flow, and the routes a
// signed-out user is meant to sit on. A background 401 must never redirect away
// from these — during startup that would override the activation routing done by
// the APP_INITIALIZER and let an unactivated device reach the login screen.
const NON_REDIRECTABLE_ROUTES = [
  '/login',
  '/env-config',
  '/activation',
  '/verify-user',
  '/unauthorized',
];

function isRedirectableRoute(url: string): boolean {
  // '/' is the transient URL while the startup navigation is still in flight.
  if (url === '/' || url === '') {
    return false;
  }
  return !NON_REDIRECTABLE_ROUTES.some(
    (route) => url === route || url.startsWith(`${route}/`) || url.startsWith(`${route}?`),
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AppAuthService);
  const router = inject(Router);
  // Skip token refresh for refresh token endpoint itself
  const isRefreshEndpoint = req.url.includes(REFRESH_TOKEN_PATH);
  const isAuthEndpoint = req.url.includes(LOCAL_AUTH_PATH);
  const isHealthEndpoint = req.url.includes(LOCAL_HEALTH_PATH);

  // Source of truth for both auth types — BEARER reads keycloak-js's in-memory token, never localStorage.
  const authHeader = authService.buildAuthHeader();

  if (authHeader && !isRefreshEndpoint && !isAuthEndpoint && !isHealthEndpoint) {
    req = req.clone({
      setHeaders: {
        Authorization: authHeader,
      },
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 401 Unauthorized errors
      if (
        error.status === 401 &&
        !isRefreshEndpoint &&
        !isAuthEndpoint &&
        !isHealthEndpoint
      ) {
        // Electron uses Basic Auth and cannot refresh its local credential.
        // Clear the stale session so the login guard does not redirect the
        // user back to a protected route with an empty permission set.
        if (authService.type === AuthType.BASIC) {
          authService.clearSession();
          // Only kick the user out of a route that actually required the
          // session. Redirecting from the setup routes would fight whatever
          // navigation put us there.
          if (isRedirectableRoute(router.url)) {
            router.navigate(['/login']);
          }
          return throwError(() => error);
        }

        // BEARER (Web): refreshToken() drives keycloak-js's updateToken(); retry with whatever comes out.
        return authService.refreshToken().pipe(
          switchMap(() => {
            const newAuthHeader = authService.buildAuthHeader();
            if (newAuthHeader) {
              const clonedRequest = req.clone({
                setHeaders: { Authorization: newAuthHeader },
              });
              return next(clonedRequest);
            }
            return throwError(() => error);
          }),
          catchError((refreshError) => {
            // Refresh failed, logout user
            authService.logout();
            return throwError(() => refreshError);
          }),
        );
      }

      // For other errors or if refresh is not possible, rethrow
      return throwError(() => error);
    })
  );
};
